import { UserRole } from '@/shared/constants/auth/auth.constant.ts';

/**
 * 사용자 권한(Role) 단일 레지스트리.
 *
 * 백엔드(auth-service)의 UserRole enum 과 값이 1:1로 일치한다.
 *   USER | PARTNER | MANAGER | ADMIN | SUPER_ADMIN
 *
 * 타입은 `shared/types/auth.type.ts` 의 `UserRole` 을 단일 소스로 재사용한다.
 * (이 파일은 "값 + 서열 + 헬퍼" 를 제공, 타입 정의는 auth.types 가 담당)
 *
 * FE는 erasableSyntaxOnly 기조이므로 enum 대신 const 객체 + 유니온 타입을 쓴다.
 */
export const USER_ROLE = {
  USER: UserRole.USER,
  PARTNER: UserRole.PARTNER,
  MANAGER: UserRole.MANAGER,
  ADMIN: UserRole.ADMIN,
  SUPER_ADMIN: UserRole.SUPER_ADMIN,
} as const satisfies Record<string, UserRole>;

/** auth.types 의 UserRole 과 동일. 기존 import 호환을 위해 별칭 유지 */
export type UserRoleValue = UserRole;

/**
 * 권한 서열. 숫자가 클수록 상위 권한.
 * "이 권한 이상이면 허용" 같은 비교(hasMinRole)에 사용. 백엔드 ROLE_LEVEL 과 동일.
 */
export const ROLE_LEVEL = {
  [USER_ROLE.USER]: 1,
  [USER_ROLE.PARTNER]: 2,
  [USER_ROLE.MANAGER]: 3,
  [USER_ROLE.ADMIN]: 4,
  [USER_ROLE.SUPER_ADMIN]: 5,
} as const satisfies Record<UserRoleValue, number>;

/** 전체 Role 을 서열 오름차순으로 나열한 배열 */
export const ROLE_VALUES = Object.keys(ROLE_LEVEL) as UserRoleValue[];

/**
 * 사용자 Role 이 기준 Role 이상인지 검사.
 * @example hasMinRole('ADMIN', 'MANAGER') // true (ADMIN >= MANAGER)
 */
export function hasMinRole(
  userRole: UserRoleValue | null | undefined,
  minRole: UserRoleValue,
): boolean {
  if (!userRole) return false;
  return ROLE_LEVEL[userRole] >= ROLE_LEVEL[minRole];
}

/** 사용자 Role 이 허용 목록에 포함되는지 검사 */
export function isRoleAllowed(
  userRole: UserRoleValue | null | undefined,
  allowed: readonly UserRoleValue[],
): boolean {
  if (!userRole) return false;
  return allowed.includes(userRole);
}
